import React from 'react';

export type EmojiPickerState = {
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (emoji: string) => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  buttonRef: React.RefObject<HTMLButtonElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  close: () => void;
  onButtonMouseDown: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onInputBlur: () => void;
};

// Основной сценарий (мышь): пользователь кликает кнопку-триггер
//   → onButtonMouseDown вызывает e.preventDefault()
//   → инпут остаётся в фокусе, выделение сохранено
//   → пикер открывается
//   → пользователь кликает эмодзи (EmojiPicker тоже вызывает preventDefault)
//   → инпут по-прежнему в фокусе; onSelect использует selectionStart/End
//
// Запасной сценарий (клавиатура / edge-case): пользователь переходит Tab в пикер, инпут теряет фокус
//   → onInputBlur сохраняет selectionStart/End в savedSelectionRef
//   → пользователь активирует эмодзи клавиатурой
//   → onSelect: focus → восстановить selection → вставить через native setter

const nativeTextareaSetter = Object.getOwnPropertyDescriptor(
  HTMLTextAreaElement.prototype,
  'value'
)!.set!;

const useEmojiPicker = (): EmojiPickerState => {
  const [isOpen, setIsOpen] = React.useState(false);

  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const savedSelectionRef = React.useRef<{ start: number; end: number } | null>(
    null
  );

  const onToggle = React.useCallback(() => setIsOpen((prev) => !prev), []);
  const close = React.useCallback(() => setIsOpen(false), []);

  const onButtonMouseDown = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    },
    []
  );

  const onInputBlur = React.useCallback(() => {
    const el = inputRef.current;
    if (el) {
      savedSelectionRef.current = {
        start: el.selectionStart,
        end: el.selectionEnd
      };
    }
  }, []);

  const onSelect = React.useCallback((emoji: string) => {
    const el = inputRef.current;
    if (!el) {
      return;
    }

    const hadFocus = document.activeElement === el;

    el.focus();

    let start = el.selectionStart;
    let end = el.selectionEnd;

    if (!hadFocus && savedSelectionRef.current) {
      start = savedSelectionRef.current.start;
      end = savedSelectionRef.current.end;
      savedSelectionRef.current = null;
    }

    const newValue = el.value.slice(0, start) + emoji + el.value.slice(end);
    const newCursor = start + emoji.length;

    nativeTextareaSetter.call(el, newValue);
    el.selectionStart = newCursor;
    el.selectionEnd = newCursor;
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return {
    isOpen,
    onToggle,
    onSelect,
    close,
    inputRef,
    buttonRef,
    containerRef,
    onButtonMouseDown,
    onInputBlur
  };
};

export default useEmojiPicker;
